import { loadEthers } from '@/utils/load-ethers';
import { getUsdcContractAddresses } from '@/utils/trade-contracts';

const ERC20_ABI = ['function balanceOf(address owner) view returns (uint256)'];
const BALANCE_OF_SELECTOR = '0x70a08231';

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

/**
 * 读取单个 USDC 合约余额
 * @param {import('ethers').ContractRunner} provider
 */
export async function readUsdcBalanceAtContract(provider, contractAddress, owner, decimals = 6) {
  const ownerAddress = normalizeAddress(owner);
  const contract = normalizeAddress(contractAddress);
  if (!provider || !contract || !ownerAddress) return 0;

  const { Contract, formatUnits } = await loadEthers();
  const token = new Contract(contract, ERC20_ABI, provider);
  const raw = await token.balanceOf(ownerAddress);
  const amount = Number(formatUnits(raw, decimals));
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * 通过钱包 provider 直接 eth_call 读 balanceOf（Privy / MetaMask 通用，不依赖外部 RPC）
 * @param {import('ethers').Eip1193Provider} injected
 */
export async function readTotalUsdcBalanceViaEthCall(injected, owner) {
  if (!injected?.request) return 0;

  const ownerAddress = normalizeAddress(owner);
  if (!ownerAddress) return 0;

  const contracts = getUsdcContractAddresses();
  if (!contracts.length) return 0;

  const { getAddress } = await loadEthers();
  let normalizedOwner = ownerAddress;
  try {
    normalizedOwner = getAddress(ownerAddress);
  } catch {
    return 0;
  }

  const paddedOwner = normalizedOwner.toLowerCase().replace(/^0x/, '').padStart(64, '0');
  let total = 0;

  for (const contractAddress of contracts) {
    try {
      const data = `${BALANCE_OF_SELECTOR}${paddedOwner}`;
      const hex = await injected.request({
        method: 'eth_call',
        params: [{ to: contractAddress, data }, 'latest'],
      });
      if (!hex || hex === '0x') continue;
      const raw = BigInt(hex);
      const amount = Number(raw) / 10 ** 6;
      if (Number.isFinite(amount)) total += amount;
    } catch (err) {
      console.warn('[wallet-usdc] eth_call failed', contractAddress, normalizedOwner, err);
    }
  }

  return Number.isFinite(total) ? total : 0;
}

function parseUsdcBalanceHex(hex) {
  if (!hex || hex === '0x') return 0;
  try {
    const raw = BigInt(hex);
    const amount = Number(raw) / 10 ** 6;
    return Number.isFinite(amount) ? amount : 0;
  } catch {
    return 0;
  }
}

function padOwnerForBalanceOf(ownerAddress) {
  return ownerAddress.toLowerCase().replace(/^0x/, '').padStart(64, '0');
}

async function ethCallBalanceOf(rpcUrl, contractAddress, ownerAddress, timeoutMs = 5000) {
  const contract = normalizeAddress(contractAddress);
  const owner = normalizeAddress(ownerAddress);
  if (!rpcUrl || !contract || !owner) return 0;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'eth_call',
        params: [
          { to: contract, data: `${BALANCE_OF_SELECTOR}${padOwnerForBalanceOf(owner)}` },
          'latest',
        ],
      }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`rpc ${res.status}`);
    const json = await res.json();
    if (json.error) throw new Error(json.error.message || 'eth_call failed');
    return parseUsdcBalanceHex(json.result);
  } catch (err) {
    if (err?.name === 'AbortError') return 0;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 轻量 RPC 读 USDC（并行 eth_call，不加载 ethers）
 */
export async function readTotalUsdcBalanceViaRpcFetch(rpcUrl, owner, { timeoutMs = 5000 } = {}) {
  const ownerAddress = normalizeAddress(owner);
  if (!ownerAddress) return 0;

  const contracts = getUsdcContractAddresses();
  if (!contracts.length) return 0;

  const amounts = await Promise.all(
    contracts.map((contractAddress) =>
      ethCallBalanceOf(rpcUrl, contractAddress, ownerAddress, timeoutMs).catch((err) => {
        if (err?.name !== 'AbortError') {
          console.warn('[wallet-usdc] rpc eth_call failed', rpcUrl, contractAddress, err);
        }
        return 0;
      })
    )
  );
  const total = amounts.reduce((sum, value) => sum + value, 0);
  return Number.isFinite(total) ? total : 0;
}

/** 汇总原生 USDC + 桥接 USDC.e（Polygon 上常见两种） */
export async function readTotalUsdcBalance(provider, owner) {
  const ownerAddress = normalizeAddress(owner);
  if (!provider || !ownerAddress) return 0;

  const contracts = getUsdcContractAddresses();
  if (!contracts.length) return 0;

  const amounts = await Promise.all(
    contracts.map((contractAddress) =>
      readUsdcBalanceAtContract(provider, contractAddress, ownerAddress, 6).catch((err) => {
        console.warn('[wallet-usdc] balanceOf failed', contractAddress, ownerAddress, err);
        return 0;
      })
    )
  );
  const total = amounts.reduce((sum, value) => sum + value, 0);
  return Number.isFinite(total) ? total : 0;
}
